import { Clock, MapPin } from 'lucide-react'

import { DetailChip, SeverityChip, StatusChip } from './DetailChips'
import { DetailPhoto } from './DetailPhoto'
import { Section } from './DetailSection'
import { IssueTimeline } from './IssueTimeline'
import { PeopleCard, type Person } from './PeopleCard'
import { RelatedIssues } from './RelatedIssues'
import { ageDays } from '../../../lib/issues'
import { prettyName } from '../../../lib/mandal-name'

import type { GisIssueDetail, GisScope, GisSite } from '../../../api/types'
import type { HodOpenRef } from '../../../lib/detail-stack'

/** Site-location marker: a map-pin + the site name, with the mandal beneath as plain text (not a
 *  navigate link). This is the ONLY place the site location is shown (the old "At a glance" grid is
 *  gone), so nothing repeats. Renders nothing when neither a site name nor a mandal is known. */
function LocationMarker({
  siteName,
  mandalLabel,
}: {
  siteName: string | null
  mandalLabel: string | null
}) {
  if (!siteName && !mandalLabel) {
    return null
  }
  return (
    <div className="mb-[22px] flex items-start gap-[9px]">
      <span className="mt-[1px] grid h-[26px] w-[26px] flex-none place-items-center rounded-lg bg-gis-red-tint text-gis-red">
        <MapPin className="h-[15px] w-[15px] stroke-[2.2]" />
      </span>
      <div className="min-w-0">
        {siteName && <div className="text-[13.5px] leading-[1.3] font-semibold text-gis-ink">{siteName}</div>}
        {mandalLabel && <div className="mt-[2px] text-[11.5px] font-medium text-gis-soft">{mandalLabel}</div>}
      </div>
    </div>
  )
}

/** Prominent description card — white to match the other section cards, with a larger, darker reading
 *  size than a plain section. */
function LeadDescription({ text }: { text: string }) {
  return (
    <div className="mb-[22px]">
      <div className="mb-[9px] text-[10.5px] font-bold tracking-[.08em] text-gis-heading uppercase">Description</div>
      <p className="text-[14px] leading-[1.55] whitespace-pre-wrap text-gis-ink">{text}</p>
    </div>
  )
}

/** The field-officer row — contact-only: the name is static (no navigation to the officer view). */
function buildFoPerson(issue: GisIssueDetail): Person {
  return {
    roleLabel: 'Field Officer',
    name: issue.officerName ?? issue.raisedByName ?? '',
    avatar: 'fo',
    phone: issue.officerPhone ?? null,
    email: issue.officerEmail ?? null,
    sub: null,
  }
}

/** The HOD row: the name links to the department workload; contact-only when there's no department. */
function buildHodPerson(issue: GisIssueDetail, onOpenHod: (hod: HodOpenRef) => void): Person {
  const department = issue.departmentName
  const phone = issue.hodPhone ?? null
  const email = issue.hodEmail ?? null
  const person: Person = {
    roleLabel: `Head of Dept${department ? ` · ${department}` : ''}`,
    name: issue.hodName ?? '',
    avatar: 'hod',
    phone,
    email,
    sub: issue.hodRole ?? null,
    openTitle: 'View HOD workload',
  }
  if (department) {
    person.onOpen = () => onOpenHod({ department, name: issue.hodName, phone, email })
  }
  return person
}

/** Responsibility roster: the field officer (static) + the department HOD (name links to its workload).
 *  The department's own issues also live in the inline "Other issues in {dept}" section. */
function buildPeople(issue: GisIssueDetail, onOpenHod: (hod: HodOpenRef) => void): Person[] {
  return [buildFoPerson(issue), buildHodPerson(issue, onOpenHod)]
}

/** Derive the few values the layout needs (age for the header chip; the site's mandal for the marker). */
function deriveModel(issue: GisIssueDetail, sites: GisSite[]) {
  const site = issue.siteId !== null ? sites.find((s) => s.id === issue.siteId) : undefined
  const mandalKey = site?.mandal ?? null
  return {
    age: ageDays(issue.raisedAt),
    mandalKey,
    mandalLabel: mandalKey ? prettyName(mandalKey) : null,
    hasPhoto: !!issue.photoUrl,
  }
}

export interface IssueDetailBodyProps {
  issue: GisIssueDetail
  sites: GisSite[]
  scope: GisScope
  /** Switch the drawer to a related issue (site/department lists). */
  onOpenIssue: (id: string) => void
  /** Open the department HOD workload (Responsibility → HOD name). */
  onOpenHod: (hod: HodOpenRef) => void
  /** Accepted for call-site compatibility (both the drawer and the inline accordion pass them) but
   *  currently unused: the field-officer name and the mandal are rendered as static text (no navigation). */
  onSelectOfficer: (id: string, name: string) => void
  onSelectMandal: (name: string) => void
}

/**
 * The issue-detail body, a single clean vertical hierarchy: title + chips → a compact photo thumbnail →
 * the site-location marker (site name · mandal link) → description → responsibility → resolution
 * timeline. The site location shows exactly once (the marker); there is no "At a glance" grid and no
 * overdue/due-soon deadline banner. The related-issue tables sit below as a supplementary explore
 * section (they power the drawer's drill-to-related-issue flow).
 */
export function IssueDetailBody({ issue, sites, scope, onOpenIssue, onOpenHod }: IssueDetailBodyProps) {
  const { age, mandalLabel, hasPhoto } = deriveModel(issue, sites)
  const people = buildPeople(issue, onOpenHod)

  return (
    <div className="@container/detail">
      {/* Header: title + severity / status / age chips. */}
      <div className="mb-5">
        <h2 className="mb-2 text-[20px] leading-[1.22] font-bold tracking-[-.02em] text-gis-ink">{issue.title}</h2>
        <div className="flex flex-wrap gap-2">
          <SeverityChip severity={issue.severity} />
          <StatusChip status={issue.status} />
          <DetailChip tabular>
            <Clock className="mr-[5px] h-[12px] w-[12px]" />
            {age} day{age === 1 ? '' : 's'} old
          </DetailChip>
        </div>
      </div>

      {/* 1. Image — full-width landscape (16:9) banner (click to zoom); omitted when there's no photo. */}
      {hasPhoto && (
        <div className="mb-4 w-full">
          <DetailPhoto photoUrl={issue.photoUrl} alt={issue.siteName ? `Site photo of ${issue.siteName}` : 'Site photo'} aspectClass="aspect-video" />
        </div>
      )}

      {/* 2. Site location — the single source for the location. */}
      <LocationMarker siteName={issue.siteName} mandalLabel={mandalLabel} />

      {/* 3. Description */}
      {issue.description && <LeadDescription text={issue.description} />}

      {/* 4. Category (survey section title) — below the description. */}
      {issue.sectionTitle && (
        <div className="mb-[22px]">
          <div className="mb-[7px] text-[10.5px] font-bold tracking-[.08em] text-gis-heading uppercase">Category</div>
          <span className="inline-flex items-center rounded-full border border-gis-line bg-white px-3 py-[5px] text-[12.5px] font-semibold text-gis-ink">
            {issue.sectionTitle}
          </span>
        </div>
      )}

      {/* 5. Responsibility */}
      <Section label="Responsibility">
        <PeopleCard people={people} />
      </Section>

      {/* 6. Resolution timeline — last, as a distinct card. */}
      <Section label="Resolution timeline" card>
        <IssueTimeline events={issue.events} />
      </Section>

      <RelatedIssues currentId={issue.id} siteId={issue.siteId} department={issue.departmentName} scope={scope} sites={sites} onOpenIssue={onOpenIssue} />
    </div>
  )
}
